import {
  ArrowPathIcon,
  ClockIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { DateRangePicker, Input, SelectItem, Tooltip } from "@heroui/react";
import AppSelect from "../../components/common/appSelect";
import { CalendarDate } from "@internationalized/date";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchActivities, fetchActivityTitles } from "../../services/api";
import { sanitizeHtml } from "../../utils/safeHtml";
import { formatTimestamp } from "../../utils/utilMethods";

interface Activity {
  id: number;
  title: string;
  description: string;
  createdAt: string;
}

interface DateRange {
  start: CalendarDate;
  end: CalendarDate;
}

const History: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<number | undefined>();
  const [activityTitles, setActivityTitles] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const { t, i18n } = useTranslation('history');

  const formatAlltext = (text?: string | null): string => {
    if (!text?.trim()) return "-";
    return text
      .split(",")
      .map((word) =>
        word
          .trim()
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase())
      )
      .join(", ");
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const formatSportName = (text: string) => {
    if (text.startsWith(t("all_sports"))) return text;
    return text
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const parseHtmlContent = (htmlString: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizeHtml(htmlString);

    const convertNode = (node: Node): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        const children = Array.from(element.childNodes).map((child) =>
          convertNode(child)
        );

        switch (tagName) {
          case 'strong':
          case 'b':
            return <strong key={Math.random()} className="font-semibold text-slate-900">{children}</strong>;
          case 'em':
          case 'i':
            return <em key={Math.random()} className="italic">{children}</em>;
          case 'u':
            return <u key={Math.random()} className="underline">{children}</u>;
          case 'br':
            return <br key={Math.random()} />;
          case 'p':
            return <p key={Math.random()} className="mb-1">{children}</p>;
          case 'span':
            return <span key={Math.random()}>{children}</span>;
          case 'div':
            return <div key={Math.random()}>{children}</div>;
          case 'h1':
            return <h1 key={Math.random()} className="text-xl font-bold mb-2">{children}</h1>;
          case 'h2':
            return <h2 key={Math.random()} className="text-lg font-bold mb-2">{children}</h2>;
          case 'h3':
            return <h3 key={Math.random()} className="text-base font-bold mb-1">{children}</h3>;
          case 'ul':
            return <ul key={Math.random()} className="list-disc ml-4 mb-2">{children}</ul>;
          case 'ol':
            return <ol key={Math.random()} className="list-decimal ml-4 mb-2">{children}</ol>;
          case 'li':
            return <li key={Math.random()} className="mb-1">{children}</li>;
          case 'a':
            return <a key={Math.random()} className="text-blue-600 underline" href={element.getAttribute('href') || '#'}>{children}</a>;
          default:
            return <span key={Math.random()}>{children}</span>;
        }
      }

      return null;
    };

    return Array.from(tempDiv.childNodes).map((node) => convertNode(node));
  };

  const formatDescriptionWithBoldLastWord = (text: string) => {
    const containsHtml = /<[^>]*>/g.test(text);

    if (containsHtml) {
      return (
        <div className="text-sm font-normal text-slate-800 font-['Plus_Jakarta_Sans']">
          {parseHtmlContent(text)}
        </div>
      );
    }

    const words = text.trim().split(" ");
    if (words.length === 0) return null;

    let lastWord = words.pop() || "";
    lastWord = lastWord.replace(/^[<>()"'.,]+|[<>()"'.,>]+$/g, "");

    const rest = words.join(" ");
    const formattedSport = formatSportName(lastWord);

    return (
      <span className="text-sm font-normal text-slate-800 font-['Plus_Jakarta_Sans'] leading-relaxed">
        {rest}{" "}
        <span className="font-semibold text-slate-900">{formattedSport}</span>
      </span>
    );
  };

  const formatDateToISO = (date: CalendarDate): string => {
    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchActivityTitles().then(setActivityTitles);
  }, []);

  useEffect(() => {
    const params: {
      title?: string;
      days?: number;
      description?: string;
      startDate?: string;
      endDate?: string;
    } = {
      title: selectedTitle || undefined,
      days: selectedDays,
      description: searchText || undefined,
    };
    if (dateRange) {
      params.startDate = formatDateToISO(dateRange.start);
      params.endDate = formatDateToISO(dateRange.end);
    }

    setLoading(true);
    fetchActivities(params)
      .then(setActivities)
      .catch((err) => console.error("Error loading activity history:", err))
      .finally(() => setLoading(false));
  }, [selectedTitle, selectedDays, searchText, dateRange]);

  const handleDateRangeChange = (range: DateRange | null) => {
    setDateRange(range);
    if (range) {
      setSelectedDays(undefined);
    }
  };

  const activeFilterCount = (selectedTitle ? 1 : 0) + (dateRange || selectedDays ? 1 : 0);

  return (
    <div className="w-full bg-[#f8fafc] min-h-screen font-['Plus_Jakarta_Sans'] pb-12">
      <div className="lg:hidden bg-white border-b border-slate-200/80 px-4 py-3 sticky top-0 z-20 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t("search_placeholder", "Search activities...")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 pl-9 pr-3 bg-slate-100 focus:bg-white rounded-2xl outline-none border border-transparent focus:border-blue-500 text-sm font-medium font-['Plus_Jakarta_Sans'] text-slate-800 placeholder:text-slate-400 transition-all"
            />
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
          </div>

          <button
            type="button"
            onClick={() => setIsMobileFilterOpen(true)}
            className={`h-10 px-3.5 rounded-2xl border flex items-center gap-1.5 text-xs font-semibold font-['Plus_Jakarta_Sans'] transition-all shrink-0 ${
              activeFilterCount > 0
                ? "bg-blue-50 border-blue-200 text-blue-600 shadow-xs"
                : "bg-slate-100 hover:bg-slate-200/80 border-slate-200/60 text-slate-700"
            }`}
          >
            <FunnelIcon className="w-4 h-4" />
            <span>{t("filter", "Filter")}</span>
            {activeFilterCount > 0 && (
              <span className="size-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                {activeFilterCount}
              </span>
            )}
          </button>

          {(selectedTitle || searchText || dateRange || selectedDays) && (
            <button
              type="button"
              onClick={() => {
                setSelectedTitle("");
                setSelectedDays(undefined);
                setSearchText("");
                setDateRange(null);
              }}
              className="size-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center shrink-0 transition-colors"
              title={t("reset_filters", "Reset filters")}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
        </div>

      </div>

      <div className="lg:hidden px-4 pt-3 flex flex-col gap-2.5">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] animate-pulse flex gap-3"
            >
              <div className="w-8 h-8 rounded-xl bg-slate-200 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-slate-200 rounded w-4/5" />
                <div className="h-3 bg-slate-100 rounded w-3/5" />
                <div className="h-2.5 bg-slate-100 rounded w-1/4 mt-2" />
              </div>
            </div>
          ))
        ) : activities.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80 shadow-sm mt-2">
            <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 mx-auto mb-3 flex items-center justify-center">
              <ClockIcon className="w-6 h-6" />
            </div>
            <p className="text-sm text-slate-600 font-medium font-['Plus_Jakarta_Sans']">
              {t("no_activities_found", "No activities found")}
            </p>
          </div>
        ) : (
          activities.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl p-3.5 border border-slate-200/80 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.04)] flex gap-3 text-left"
            >
              <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                <ClockIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-normal text-slate-800 leading-snug">
                  {formatDescriptionWithBoldLastWord(item.description)}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-400 tabular-nums">
                  <span>{formatTimestamp(item.createdAt)}</span>
                  {item.title && (
                    <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-[10px] font-medium text-slate-600">
                      {formatAlltext(item.title)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center lg:hidden">
          <div className="w-full bg-white rounded-t-3xl p-5 shadow-2xl flex flex-col gap-4 max-h-[85vh] overflow-y-auto no-scrollbar">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="text-base font-semibold font-['Plus_Jakarta_Sans'] text-slate-900">
                {t("filters", "Filters")}
              </h3>
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-2">
                  {t("select_title_placeholder", "Select Title")}
                </label>
                <AppSelect
                  placeholder={t("select_title_placeholder", "Select Title")}
                  className="w-full font-['Plus_Jakarta_Sans']"
                  size="sm"
                  selectedKeys={selectedTitle ? [selectedTitle] : []}
                  onChange={(e) => setSelectedTitle(e.target.value)}
                  classNames={{
                    trigger: "h-10 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 data-[focus=true]:border-blue-400 data-[focus=true]:bg-white shadow-none",
                    value: "text-sm text-slate-700 font-['Plus_Jakarta_Sans']",
                  }}
                >
                  {activityTitles.map((title) => (
                    <SelectItem key={title} className="font-['Plus_Jakarta_Sans'] text-sm">
                      {formatAlltext(title)}
                    </SelectItem>
                  ))}
                </AppSelect>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-2">
                  {t("date_range", "Date Range")}
                </label>
                <DateRangePicker
                  className="w-full font-['Plus_Jakarta_Sans']"
                  size="sm"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  showMonthAndYearPickers
                  visibleMonths={1}
                  classNames={{
                    inputWrapper: "h-10 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 data-[focus-within=true]:border-blue-400 data-[focus-within=true]:bg-white shadow-none",
                    input: "text-sm text-slate-700 font-['Plus_Jakarta_Sans']",
                  }}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-slate-100 mt-2">
              <button
                type="button"
                onClick={() => {
                  setSelectedTitle("");
                  setSelectedDays(undefined);
                  setSearchText("");
                  setDateRange(null);
                }}
                className="flex-1 h-11 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors"
              >
                {t("reset_filters", "Reset")}
              </button>
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(false)}
                className="flex-1 h-11 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-colors"
              >
                {t("apply", "Apply")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="hidden lg:block w-full bg-white border-b border-slate-200/80 px-4 sm:px-6 py-4 mb-6">
        <div className="max-w-full mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] shrink-0">History</h1>
          <div className="flex flex-wrap items-center gap-2.5">
            <Input
              placeholder={t("search_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />
              }
              className="w-64 min-w-64 font-['Plus_Jakarta_Sans']"
              size="sm"
              classNames={{
                inputWrapper: "h-9 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 focus-within:!border-blue-400 focus-within:!bg-white shadow-none",
                input: "text-sm text-slate-700 placeholder:text-slate-400 font-['Plus_Jakarta_Sans']",
              }}
            />

            <AppSelect
              placeholder={t("select_title_placeholder", "Select Title")}
              className="w-56 min-w-56 font-['Plus_Jakarta_Sans']"
              size="sm"
              selectedKeys={selectedTitle ? [selectedTitle] : []}
              onChange={(e) => setSelectedTitle(e.target.value)}
              classNames={{
                trigger: "h-9 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 data-[focus=true]:border-blue-400 data-[focus=true]:bg-white shadow-none",
                value: "text-sm text-slate-700 font-['Plus_Jakarta_Sans']",
              }}
            >
              {activityTitles.map((title) => (
                <SelectItem key={title} className="font-['Plus_Jakarta_Sans'] text-sm">
                  {formatAlltext(title)}
                </SelectItem>
              ))}
            </AppSelect>

            <DateRangePicker
              className="w-64 font-['Plus_Jakarta_Sans']"
              size="sm"
              value={dateRange}
              onChange={handleDateRangeChange}
              showMonthAndYearPickers
              visibleMonths={2}
              classNames={{
                inputWrapper: "h-9 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 data-[focus-within=true]:border-blue-400 data-[focus-within=true]:bg-white shadow-none",
                input: "text-sm text-slate-700 font-['Plus_Jakarta_Sans']",
              }}
            />

            {(selectedTitle || searchText || dateRange) && (
              <Tooltip content={t("reset_filters")}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTitle("");
                    setSelectedDays(undefined);
                    setSearchText("");
                    setDateRange(null);
                  }}
                  className="p-1.5 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
                  title={t("reset_filters", "Reset filters")}
                >
                  <ArrowPathIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block px-4 sm:px-6">
        {loading ? (
          <div className="p-1 bg-[#f4f5f7] rounded-[20px] border border-slate-200/80 overflow-hidden shadow-sm animate-pulse">
            <div className="bg-white rounded-2xl flex flex-col divide-y divide-slate-100 shadow-sm overflow-hidden">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="p-4 sm:px-6 sm:py-4.5 space-y-2">
                  <div className="h-4 w-3/4 bg-slate-200 rounded" />
                  <div className="h-3 w-1/4 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80 shadow-sm">
            <p className="text-sm text-slate-500 font-medium font-['Plus_Jakarta_Sans']">
              {t("no_activities_found")}
            </p>
          </div>
        ) : (
          <div className="p-1 bg-[#f4f5f7] rounded-[20px] border border-slate-200/80 overflow-hidden shadow-sm">
            <div className="bg-white rounded-2xl flex flex-col divide-y divide-slate-100 shadow-sm overflow-hidden">
              {activities.map((item) => (
                <div
                  key={item.id}
                  className="p-4 sm:px-6 sm:py-4 hover:bg-slate-50/70 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-left"
                >
                  <div className="flex-1 text-sm font-normal text-slate-800 font-['Plus_Jakarta_Sans'] leading-relaxed">
                    {formatDescriptionWithBoldLastWord(item.description)}
                  </div>
                  <div className="shrink-0 text-xs font-normal text-slate-400 font-['Plus_Jakarta_Sans'] tabular-nums">
                    {formatTimestamp(item.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
